import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Controls from "./Controls";
import { Helmet, HelmetProvider } from "react-helmet-async";

import { Text, Circle, Group, Layer, Line, Rect, Stage } from "react-konva";
import Konva from "konva";

class Point {
  x: number;
  y: number;
  obstacle: boolean;
  constructor(x, y, obstacle) {
    this.x = x;
    this.y = y;
    this.obstacle = obstacle;
  }
  distance(p: Point) {
    return Math.sqrt((this.x - p.x) ** 2 + (this.y - p.y) ** 2);
  }
}

class Edge {
  start: Point;
  end: Point;
  weight: number;
  constructor(start, end, weight) {
    this.start = start;
    this.end = end;
    this.weight = weight;
  }
}

const point1 = new Point(-3, -3, false);
const point2 = new Point(-1, -3, true);
const point3 = new Point(1, -3, true);
const point4 = new Point(3, -3, false);
const point5 = new Point(-3, -1, false);
const point6 = new Point(-1, -1, false);
const point7 = new Point(1, -1, false);
const point8 = new Point(3, -1, false);
const point9 = new Point(-3, 1, false);
const point10 = new Point(-1, 1, true);
const point11 = new Point(1, 1, true);
const point12 = new Point(3, 1, false);
const point13 = new Point(-3, 3, false);
const point14 = new Point(-1, 3, false);
const point15 = new Point(1, 3, true);
const point16 = new Point(3, 3, true);

const visitedPoints = (step, i) => {
  if (step == 1 && i == 0) return true;
  else if (step == 2 && (i == 0 || i == 4)) return true;
  else if (step == 3 && (i == 2 || i == 4 || i == 5)) return true;
  else if (step == 4 && (i == 2 || i == 4 || i == 5 || i == 8)) return true;
  else if (step == 5 && (i == 2 || i == 4 || i == 5 || i == 8 || i == 6))
    return true;
  else if (
    step == 6 &&
    (i == 2 || i == 4 || i == 5 || i == 8 || i == 6 || i == 12)
  )
    return true;
  else if (
    step == 7 &&
    (i == 2 || i == 4 || i == 5 || i == 8 || i == 6 || i == 12 || i == 7)
  )
    return true;
  else if (
    step == 8 &&
    (i == 2 ||
      i == 4 ||
      i == 5 ||
      i == 8 ||
      i == 6 ||
      i == 12 ||
      i == 7 ||
      i == 13)
  )
    return true;
  else if (step >= 9) return true;
  else return false;
};

const pointHighlight = (step, i) => {
  if (step == 1 && i == 0) return true;
  else if (step == 2 && (i == 0 || i == 2)) return true;
  else if (step == 3 && (i == 2 || i == 3)) return true;
  else if (step == 4 && (i == 2 || i == 6)) return true;
  else if (step == 5 && (i == 3 || i == 4)) return true;
  else if (step == 6 && (i == 6 || i == 8)) return true;
  else if (step == 7 && (i == 4 || i == 5)) return true;
  else if (step == 8 && (i == 8 || i == 9)) return true;
  else if (step == 9 && (i == 1 || i == 5)) return true;
  else if (step == 10 && (i == 5 || i == 7)) return true;
  else if (step == 11 && i == 9) return true;
  else if (step == 12 && i == 1) return true;
  else if (step == 13 && (i == 7 || i == 10)) return true;
  else if (step == 14 && i == 10) return true;
  else return false;
};

const edgeHighlight = (step, i) => {
  if (step == 2 && i == 3) return true;
  else if (step == 3 && i == 0) return true;
  else if (step == 4 && i == 5) return true;
  else if (step == 5 && i == 1) return true;
  else if (step == 6 && i == 7) return true;
  else if (step == 7 && i == 2) return true;
  else if (step == 8 && i == 8) return true;
  else if (step == 9 && i == 4) return true;
  else if (step == 10 && i == 6) return true;
  else if (step == 13 && i == 9) return true;
  else return false;
};

function Main() {
  const [points, setPoints] = useState<Point[]>([
    point1,
    point2,
    point3,
    point4,
    point5,
    point6,
    point7,
    point8,
    point9,
    point10,
    point11,
    point12,
    point13,
    point14,
    point15,
    point16,
  ]);
  const [edges, setEdges] = useState<Edge[]>([
    new Edge(point5, point6, 1),
    new Edge(point6, point7, 1),
    new Edge(point7, point8, 1),
    new Edge(point1, point5, 1),
    new Edge(point4, point8, 1),
    new Edge(point5, point9, 1),
    new Edge(point8, point12, 1),
    new Edge(point9, point13, 1),
    new Edge(point13, point14, 1),
  ]);
  const divRef = useRef(null);

  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [gridRatio, setGridRatio] = useState(0);
  const groupRef: React.RefObject<Konva.Group> = useRef();

  useEffect(() => {
    if (divRef.current?.offsetHeight && divRef.current?.offsetWidth) {
      setDimensions({
        width: divRef.current.offsetWidth,
        height: divRef.current.offsetHeight,
      });
      setGridRatio(divRef.current.offsetWidth / 12);
    }
    if (divRef.current?.offsetHeight == 0 && divRef.current?.offsetWidth) {
      setDimensions({
        width: divRef.current.offsetWidth,
        height: divRef.current.offsetWidth,
      });
      setGridRatio(divRef.current.offsetWidth / 12);
    }
  }, [divRef]);
  const [showDistance, setShowDistance] = useState(false);

  const cleanAllPoints = () => {
    setPoints([]);
  };

  const [step, setStep] = useState(0);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "column", md: "row" },
        flexGrow: 1,
      }}
    >
      <Box sx={{ flex: 8 }}>
        <Controls step={step} setStep={setStep} />
      </Box>
      <Box
        sx={{
          flex: 12,
          minHeight: 600,
          backgroundColor: "#efefef",
          position: "relative",
          height: "100%",
        }}
        ref={divRef}
      >
        <Box
          sx={{
            position: "absolute",
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "center",
            margin: 2,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginRight: 5,
            }}
          ></Box>
        </Box>
        <Stage
          width={dimensions.width}
          height={dimensions.height}
          scaleY={-1}
          offsetX={-dimensions.width / 2}
          offsetY={dimensions.height / 2}
        >
          <Layer>
            <Group ref={groupRef}>
              {points.map((p, i) => (
                <Group key={"pointr" + i}>
                  <Rect
                    x={p.x * gridRatio}
                    y={p.y * gridRatio}
                    width={2 * gridRatio}
                    height={2 * gridRatio}
                    offsetX={gridRatio}
                    offsetY={gridRatio}
                    fill={
                      p.obstacle
                        ? "gray"
                        : i == 0
                        ? "orange"
                        : i == 11
                        ? "#00e676"
                        : visitedPoints(step, i)
                        ? "#35baf6"
                        : "white"
                    }
                    stroke="black"
                  />
                  {i == 0 && (
                    <Text
                      scaleY={-1}
                      fontSize={25}
                      align="center"
                      verticalAlign="middle"
                      fill={"black"}
                      fillAfterStrokeEnabled={true}
                      width={50}
                      height={50}
                      x={p.x * gridRatio - 25}
                      y={p.y * gridRatio - 25}
                      text={"start"}
                    />
                  )}
                  {i == 11 && (
                    <Text
                      scaleY={-1}
                      fontSize={25}
                      align="center"
                      verticalAlign="middle"
                      fill={"black"}
                      fillAfterStrokeEnabled={true}
                      width={100}
                      height={50}
                      x={p.x * gridRatio - 50}
                      y={p.y * gridRatio + 75}
                      text={"target"}
                    />
                  )}
                </Group>
              ))}
              {edges.map((e, i) => {
                let startx = e.start.x * gridRatio;
                let starty = e.start.y * gridRatio;
                let endx = e.end.x * gridRatio;
                let endy = e.end.y * gridRatio;
                return (
                  <Group key={"edge" + i}>
                    <Line
                      points={[startx, starty, endx, endy]}
                      strokeWidth={5}
                      stroke={edgeHighlight(step, i) ? "red" : "black"}
                    />
                  </Group>
                );
              })}
              {points
                .filter((p) => !p.obstacle)
                .map((p, i) => {
                  return (
                    <Group key={"point" + i}>
                      <Circle
                        radius={gridRatio / 3 < 20 ? 20 : gridRatio / 3}
                        strokeWidth={5}
                        stroke={pointHighlight(step, i) ? "red" : "black"}
                        fill="white"
                        x={p.x * gridRatio}
                        y={p.y * gridRatio}
                      />
                      <Text
                        scaleY={-1}
                        fontSize={25}
                        align="center"
                        verticalAlign="middle"
                        fill={pointHighlight(step, i) ? "red" : "black"}
                        fillAfterStrokeEnabled={true}
                        width={50}
                        height={50}
                        x={p.x * gridRatio - 25}
                        y={p.y * gridRatio + 25}
                        text={(i + 10).toString(36).toUpperCase()}
                      />
                    </Group>
                  );
                })}
            </Group>
          </Layer>
        </Stage>
      </Box>
    </Box>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
}

function DijkstrasForGrids(props: TabPanelProps) {
  const { children, ...other } = props;

  return (
    <HelmetProvider>
      <Main />
      <Helmet>
        <title>Visualization of Dijstra's Algorithm For Grids</title>
        <meta
          name="description"
          content="Explore interactive visualization and tutorial of Dijkstra's Algorithm for grids: Learn how Dijkstra's Algorithm works on a grid map with a complete explanation and step-by-step guide. Visualize table changes and graph highlights to understand the algorithm's process. Ideal for mastering efficient pathfinding techniques in robotics, computer science and mathematics."
        />
      </Helmet>
    </HelmetProvider>
  );
}

export default DijkstrasForGrids;
